import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ScoreRing from "../components/ScoreRing";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Loader2, LogOut, ShieldCheck, ArrowRight, CircleCheck, Clock, Circle, Download, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const CATS = ["AI Visibility", "AEO/GEO", "Structured Data", "Agent Discovery", "Transaction Readiness", "Payment Readiness", "Fulfillment", "Policy Clarity"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role === "founder_admin") { navigate("/admin"); return; }
    api.get("/customer/overview").then(({ data }) => setData(data)).catch(() => setData({ has_org: false })).finally(() => setReady(true));
  }, [user, loading, navigate]);

  if (!ready || !data) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar user={user} logout={logout} />
      {data.blueprint_credit_expires_at && (
        <div className="max-w-6xl mx-auto px-4 mt-6"><div className="rounded-xl border border-orange-200 bg-orange-50 px-5 py-3 text-sm font-medium text-slate-800" data-testid="portal-blueprint-credit">Your $497 is credited toward any plan until {new Date(data.blueprint_credit_expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.</div></div>
      )}
      {!data.has_org ? (
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-slate-900">No active implementation yet</h1>
          <p className="text-slate-500 mt-2">Run a free scan and choose a plan to get started. After payment, your project appears here automatically.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button onClick={() => navigate("/free-scan")} className="rounded-full bg-indigo-600 hover:bg-indigo-700">Scan My Site Free</Button>
            <Button variant="outline" onClick={() => navigate("/pricing")} className="rounded-full">View Plans</Button>
          </div>
        </div>
      ) : <CustomerBody data={data} navigate={navigate} />}
    </div>
  );
}

function TopBar({ user, logout }) {
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center"><span className="text-white font-heading font-bold">A</span></div>
          <span className="font-heading font-bold text-slate-900">Make My Business AI Ready <span className="text-indigo-600 font-semibold">(AgenticReady)</span> <span className="text-slate-400 font-normal text-sm">Portal</span></span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-1" /> Log out</Button>
        </div>
      </div>
    </header>
  );
}

function CustomerBody({ data, navigate }) {
  const { organization: org, plan, project, subscription: sub, scan } = data;
  const [running, setRunning] = useState(false);
  const download = (name, content) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };
  const runProject = async () => {
    setRunning(true);
    toast.info("Agents generating deliverables + running QA… this runs in the background.");
    try {
      await api.post("/customer/project/run");
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const { data } = await api.get("/customer/overview");
          if (data?.project?.artifacts) { clearInterval(poll); toast.success("Implementation complete"); setTimeout(() => window.location.reload(), 600); }
          else if (tries > 30) { clearInterval(poll); setRunning(false); toast.message("Still working — refresh in a moment to see deliverables."); }
        } catch { /* keep polling */ }
      }, 5000);
    } catch { toast.error("Run failed"); setRunning(false); }
  };
  const initial = project?.initial_score ?? 42;
  const current = project?.current_score ?? initial;
  const improvement = current - initial;
  const verified = current >= 90;
  const catScores = {};
  (scan?.result?.category_meta || []).forEach((c) => { catScores[c.name] = c.score; });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="dash-org-name">{org.display_name}</h1>
          <p className="text-slate-500 text-sm">{org.domain || "—"} · {plan?.name}</p>
        </div>
        <Badge className={verified ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/30" : "bg-slate-100 text-slate-600"}>
          <ShieldCheck className="h-3.5 w-3.5 mr-1" /> {verified ? "AgenticReady Verified™" : "Verification pending"}
        </Badge>
      </div>

      {!data.onboarding_complete && (
        <div className="mt-6 rounded-2xl border-2 border-indigo-200 bg-indigo-50/50 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-heading font-semibold text-slate-900">Complete your onboarding</p>
            <p className="text-sm text-slate-600 mt-1">Answer a few questions so our agents can begin authorized implementation. No call required.</p>
          </div>
          <Button onClick={() => navigate(`/onboarding?org=${org.id}`)} data-testid="start-onboarding-btn" className="rounded-full bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap">Start Onboarding <ArrowRight className="h-4 w-4 ml-1" /></Button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col items-center">
          <p className="font-mono text-xs uppercase tracking-wide text-slate-400 mb-3">Current Score</p>
          <ScoreRing score={current} size={150} />
          <p className="text-sm text-slate-500 mt-3">Initial {initial} · {improvement >= 0 ? "+" : ""}{improvement} improvement</p>
        </div>
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          <StatCard label="Plan" value={plan?.name} sub={`$${Number(plan?.monthly_price).toLocaleString()}/mo monitoring`} />
          <StatCard label="Implementation" value={(project?.status || "—").replace(/_/g, " ")} sub={`${project?.automation_percentage || 0}% automated`} />
          <StatCard label="Monitoring" value={sub?.status?.replace(/_/g, " ") || "—"} sub="Continuous health checks" />
          <StatCard label="Target Score" value={project?.target_score || 92} sub="AgenticReady Verified at 90+" />
        </div>
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="flex-wrap h-auto">
          {["overview", "issues", "implementation", "monitoring", "reports", "billing", "support"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="capitalize">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATS.map((c) => (
              <div key={c} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs text-slate-500">{c}</p>
                <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{catScores[c] ?? "—"}</p>
                <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className="h-full bg-indigo-500 rounded-full" style={{ width: `${catScores[c] || 0}%` }} /></div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="issues" className="mt-5 space-y-3">
          {(scan?.result?.gaps || []).map((g, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 flex justify-between items-start gap-3">
              <div><p className="font-semibold text-slate-900">{g.title}</p><p className="text-sm text-slate-500 mt-1">{g.business_description}</p></div>
              <Badge variant="outline" className="capitalize">{g.severity}</Badge>
            </div>
          ))}
          {!scan && <Empty text="Issues appear after your initial scan is linked." />}
        </TabsContent>

        <TabsContent value="implementation" className="mt-5 space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">{project?.verified ? "AgenticReady Verified™ issued." : "Run the autonomous implementation to generate deliverables, QA, and re-score."}</p>
            <Button onClick={runProject} disabled={running} data-testid="run-implementation-btn" className="rounded-full bg-indigo-600 hover:bg-indigo-700">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4 mr-1" /> {project?.artifacts ? "Re-run" : "Run"} Implementation</>}
            </Button>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
            {(project?.tasks || []).map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-4" data-testid={`task-${t.agent}`}>
                {t.status === "completed" ? <CircleCheck className="h-5 w-5 text-emerald-500" /> : t.status === "pending" ? <Clock className="h-5 w-5 text-amber-500" /> : <Circle className="h-5 w-5 text-slate-300" />}
                <div className="flex-1"><p className="font-medium text-slate-800 text-sm">{t.title}</p><p className="text-xs text-slate-400 font-mono">{t.agent}</p></div>
                <Badge variant="outline" className="capitalize text-xs">{t.status}</Badge>
              </div>
            ))}
          </div>
          {project?.qa && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="qa-results">
              <p className="font-heading font-semibold text-slate-900">Independent QA — {project.qa.tests_passed}/{project.qa.tests_total} passed</p>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {project.qa.results.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-600">{r.passed ? <CircleCheck className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-rose-400" />}{r.test}</div>
                ))}
              </div>
            </div>
          )}
          {project?.artifacts && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="deliverables">
              <p className="font-heading font-semibold text-slate-900 mb-1">Deliverables — Make My Business AI Ready (AIH LLC)</p>
              <p className="text-sm text-slate-500 mb-4">{project.artifacts.summary}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[["Schema.org JSON-LD", "schema.jsonld.html", project.artifacts.schema_jsonld],
                  ["llms.txt (agent index)", "llms.txt", project.artifacts.llms_txt],
                  ["robots.txt (AI crawlers)", "robots.txt", project.artifacts.robots_txt],
                  ["AgenticReady Manifest", "agent-manifest.json", project.artifacts.agent_manifest],
                  ["Direct-answer FAQ", "faq.txt", project.artifacts.faq_markup]].filter(([, , c]) => c).map(([label, fn, content]) => (
                  <button key={fn} onClick={() => download(fn, content)} data-testid={`download-${fn}`} className="flex items-center justify-between rounded-lg border border-slate-200 hover:border-indigo-400 px-4 py-3 text-left transition-colors">
                    <span className="text-sm font-medium text-slate-700">{label}</span><Download className="h-4 w-4 text-indigo-600" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="monitoring" className="mt-5"><Empty text="Continuous monitoring activates after deployment. Health checks run monthly." /></TabsContent>
        <TabsContent value="reports" className="mt-5"><Empty text="Your monthly readiness report will appear here automatically." /></TabsContent>
        <TabsContent value="billing" className="mt-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Setup fee (paid)</span><span className="font-semibold">${Number(plan?.setup_price).toLocaleString()}</span></div>
            <div className="flex justify-between py-2"><span className="text-slate-500">Monthly monitoring</span><span className="font-semibold">${Number(plan?.monthly_price).toLocaleString()}/mo</span></div>
          </div>
        </TabsContent>
        <TabsContent value="support" className="mt-5"><Empty text="Message us any time. Async founder escalation is available for unusual situations." /></TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="font-heading text-lg font-bold text-slate-900 mt-1 capitalize">{value}</p>
      <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}
function Empty({ text }) { return <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400 text-sm">{text}</div>; }
